import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import {
    addAlert,
    setFeedbackContext,
    setFeedbackIsOpen,
    setFeedbackIsPositive,
} from '../../redux/features/app/appSlice';
import { updateMessageProperty } from '../../redux/features/conversations/conversationsSlice';
import { AlertType } from '../models/AlertType';
import { IChatMessage, UserFeedback, UserFeedbackSource } from '../models/ChatMessage';
import { ConflictError } from '../services/BaseService';
import { useChat } from './useChat';

export interface UseMessageFeedbackOptions {
    /**
     * Which surface this rating comes from — `'thumbs'` for the in-message 👍/👎, `'header'` for
     * the embedded-chat header prompt. Sent with the rating so the cohorts can be distinguished in
     * analytics (mirrors CSAT's modal/inline source).
     */
    source: UserFeedbackSource;
    /**
     * Open the optional free-text comment dialog after recording the rating. The in-message thumbs
     * do this; the inline-header Ja/Nej deliberately doesn't (it should just register the answer).
     */
    openCommentDialog?: boolean;
    /**
     * Surface a failure alert (and roll back the optimistic flip) for *every* error, not only a
     * 409 conflict. The inline header wants this so a failed write never looks saved; the thumbs
     * historically alerted only on a concurrency conflict and swallowed other errors.
     */
    alertOnAnyError?: boolean;
    /** Called once the server write actually lands — e.g. to show a brief "Tack!" beat. */
    onSuccess?: (messageId: string) => void;
}

/**
 * Records a thumbs/Ja-Nej rating for a bot message following the optimistic-concurrency contract
 * from #1566: optimistically flip `userFeedback` so the UI reacts immediately, then roll the flip
 * back and alert if the server write fails. Shared by the in-message thumbs (UserFeedbackActions)
 * and the inline-header prompt (ChatRoomInlineHeader) so that contract lives in exactly one place
 * — a future change can't be remembered in one copy and forgotten in the other.
 */
export const useMessageFeedback = ({
    source,
    openCommentDialog = false,
    alertOnAnyError = false,
    onSuccess,
}: UseMessageFeedbackOptions) => {
    const chat = useChat();
    const dispatch = useAppDispatch();
    const selectedId = useAppSelector((state: RootState) => state.conversations.selectedId);
    const { t } = useTranslation();

    return useCallback(
        (message: IChatMessage, positive: boolean) => {
            const messageId = message.id;
            if (!messageId) {
                return;
            }

            if (openCommentDialog) {
                dispatch(setFeedbackIsPositive(positive));
                dispatch(
                    setFeedbackContext({
                        chatId: selectedId,
                        messageId,
                        contentMetadata: message.contentMetadataArray?.contentMetadata,
                    }),
                );
                dispatch(setFeedbackIsOpen({ isOpen: true, hideRating: true }));
            }

            // Optimistic UI flip — captured up front so we can roll back if the server write fails.
            const previousFeedback = message.userFeedback;
            const newFeedback = positive ? UserFeedback.Positive : UserFeedback.Negative;
            dispatch(
                updateMessageProperty({
                    chatId: selectedId,
                    messageIdOrIndex: messageId,
                    property: 'userFeedback',
                    value: newFeedback,
                    frontLoad: true,
                }),
            );

            chat.userFeedbackOnMessage(message.chatId, messageId, newFeedback, source)
                .then(() => {
                    // Confirm only once the write actually lands. (Confirming up front would let a
                    // failed/rolled-back write still flash a success beat.)
                    onSuccess?.(messageId);
                })
                .catch((e: unknown) => {
                    const isConflict = e instanceof ConflictError;
                    // The thumbs only ever rolled back on a 409 conflict and stayed silent on other
                    // errors; the header opts into rolling back + alerting on any failure so a write
                    // that didn't land never looks saved.
                    if (!isConflict && !alertOnAnyError) {
                        return;
                    }
                    dispatch(
                        updateMessageProperty({
                            chatId: selectedId,
                            messageIdOrIndex: messageId,
                            property: 'userFeedback',
                            value: previousFeedback,
                            frontLoad: true,
                        }),
                    );
                    dispatch(
                        addAlert({
                            message: t(
                                isConflict ? 'chatHistory.feedbackConcurrentEdit' : 'chatHistory.feedbackFailed',
                            ),
                            type: AlertType.Error,
                        }),
                    );
                });
        },
        [chat, dispatch, selectedId, t, source, openCommentDialog, alertOnAnyError, onSuccess],
    );
};
